import React, { useState } from 'react';
import { Button, Modal, Checkbox, message } from 'antd';
import { DownloadOutlined } from "@ant-design/icons";
import { useFirestore } from 'react-redux-firebase';
import { resolveAttachments, downloadZipAndSave, emptyFolderPaths } from '../../lib/folder_zip';
import { collectSubtreeFiles } from '../../lib/folder_tree';
import { FILE_HYDRATION_BATCH } from '../../lib/folder_listing';

const sectionStyle = { fontSize: 12, fontWeight: 600, color: 'rgba(0, 0, 0, 0.55)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '14px 0 6px' };
const hintStyle = { fontSize: 12, color: 'rgba(0, 0, 0, 0.45)', lineHeight: 1.4 };

const DownloadFolder = ({ folder, filesAllowed = null, pruneSubtree = null }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [modal, setModal] = useState({ open: false, step: 'options' });
  const NO_OPTIONS = { xml: false, accounting: false, links: false, emptyFolders: false };
  const [options, setOptions] = useState(NO_OPTIONS);
  const [progress, setProgress] = useState({ percent: 0, current: 0, total: 0, folderLabel: '', fileName: '', phase: 'census' });
  const firestore = useFirestore();

  // Censo: documentos + adjuntos (total fijo antes de descargar).
  // `resolveAttachments` necesita el doc completo de files/{id}; el recorrido
  // del subárbol solo trae { name, url, fileId, ref }, así que ese doc se lee
  // SOLO si el usuario pidió algún adjunto, y en tandas paralelas. Sin
  // casillas marcadas: 0 lecturas extra.
  const buildWorkList = async (subtreeFiles, opts) => {
    const workList = [];
    const wantAttachments = opts.xml || opts.accounting || opts.links;

    subtreeFiles.forEach(f => {
      workList.push({ name: f.name, url: f.url, zipPath: f.zipPath, folderLabel: f.zipPath || folder.name });
    });

    if (!wantAttachments) return workList;

    for (let i = 0; i < subtreeFiles.length; i += FILE_HYDRATION_BATCH) {
      const batch = subtreeFiles.slice(i, i + FILE_HYDRATION_BATCH);
      // eslint-disable-next-line no-await-in-loop
      const snaps = await Promise.all(batch.map(f => f.ref.get().catch(() => null)));
      for (let k = 0; k < batch.length; k += 1) {
        const snap = snaps[k];
        if (!snap || snap.exists === false) continue;
        const f = batch[k];
        // eslint-disable-next-line no-await-in-loop
        const atts = await resolveAttachments(snap.data(), opts);
        atts.forEach(a => workList.push({ ...a, zipPath: f.zipPath, folderLabel: f.zipPath || folder.name }));
      }
    }
    return workList;
  };

  async function downloadFolder() {
    try {
      setIsDownloading(true);
      setModal({ open: true, step: 'progress' });
      setProgress({ percent: 0, current: 0, total: 0, folderLabel: '', fileName: '', phase: 'census' });

      // Diagnóstico: dump del shape de folder.reference para identificar
      // entradas mal formadas en producción.
      console.log('[DOWNLOAD] folder.name:', folder?.name);
      console.log('[DOWNLOAD] folder.reference shape:', {
        hasReference: !!folder?.reference,
        referenceKeys: folder?.reference ? Object.keys(folder.reference) : null,
        id: folder?.reference?.id,
        pathType: typeof folder?.reference?.path,
        pathHasCollection: typeof folder?.reference?.path?.collection === 'function',
        pathValue: folder?.reference?.path?.path || folder?.reference?.path
      });

      // Validaciones defensivas: la matriz puede traer entradas legacy
      // sin `reference` o con `reference.path` como string en lugar de DocRef.
      if (!folder?.reference || !folder.reference.id) {
        throw new Error('La carpeta no tiene una referencia válida en Firestore.');
      }

      // En SDK compat, `folder.reference.path` es un DocumentReference y
      // tiene método .collection(). Verificamos antes de invocar.
      let parentDocRef = folder.reference.path;
      if (typeof parentDocRef?.collection !== 'function') {
        // Caso degradado: path quedó serializado como string. Lo
        // reconstruimos via firestore.doc(<string>).
        if (typeof parentDocRef === 'string') {
          console.log('[DOWNLOAD] reconstruyendo DocumentReference desde string path:', parentDocRef);
          parentDocRef = firestore.doc(parentDocRef);
        } else {
          console.error('[DOWNLOAD] reference.path no es DocRef ni string:', parentDocRef);
          throw new Error('La referencia de la carpeta no es válida (path inesperado).');
        }
      }

      // Recorrido del subárbol pedido. Antes esto era
      // files.where('customer','==',customerRoot).get(), que traía TODOS los
      // archivos del cliente sea cual sea la subcarpeta: medio millón de docs
      // en el cliente grande para bajar diez archivos. Ahora las lecturas son
      // proporcionales al subárbol. Ver lib/folder_tree.js.
      const { files: subtreeFiles, folders: subtreeFolders, reads, failed, skippedFolders, allowedFolders } = await collectSubtreeFiles({
        parentDoc: parentDocRef,
        collectionId: folder.reference.id,
        folderName: folder.name,
        rootEntry: folder,
        filesAllowed,
        pruneSubtree,
      });
      console.log('[DOWNLOAD] censo del subarbol:', { archivos: subtreeFiles.length, reads, failed, carpetasSinPermiso: skippedFolders });

      // Con «Incluir las carpetas vacías» se descarga aunque no haya archivos:
      // el ZIP trae solo la estructura.
      if (subtreeFiles.length === 0 && !(options.emptyFolders && subtreeFolders.length > 0)) {
        throw new Error(skippedFolders > 0 && allowedFolders === 0
          ? 'No tienes permiso para descargar los archivos de esta carpeta'
          : 'No se encontraron archivos en esta carpeta');
      }

      const workList = await buildWorkList(subtreeFiles, options);
      const emptyFolders = options.emptyFolders ? emptyFolderPaths(subtreeFolders, workList) : [];

      const { skipped } = await downloadZipAndSave({
        items: workList,
        emptyFolders,
        zipName: folder.name,
        onProgress: p => setProgress({ ...p, phase: 'download' })
      });

      if (skipped.length > 0) {
        message.warning(`Descarga completada — ${skipped.length} archivo(s) no se pudieron incluir`);
      } else {
        message.success('Descarga completada');
      }
    } catch (error) {
      message.error(error.message || 'Error al descargar la carpeta');
    } finally {
      setIsDownloading(false);
      setModal({ open: false, step: 'options' });
      setProgress({ percent: 0, current: 0, total: 0, folderLabel: '', fileName: '', phase: 'census' });
    }
  }

  return (
    <>
      <Button
        type="link"
        style={{ paddingLeft: 0 }}
        disabled={isDownloading}
        onClick={() => { setOptions(NO_OPTIONS); setModal({ open: true, step: 'options' }); }}
      >
        <DownloadOutlined style={{ marginRight: 8 }} />
        {isDownloading ? 'Descargando...' : 'Descargar carpeta'}
      </Button>

      <Modal
        title={modal.step === 'options' ? `Descargar carpeta "${folder.name}"` : `Descargando "${folder.name}"`}
        open={modal.open}
        closable={false}
        maskClosable={false}
        footer={modal.step === 'options' ? undefined : null}
        okText="Descargar"
        cancelText="Cancelar"
        onOk={() => downloadFolder()}
        onCancel={() => setModal({ open: false, step: 'options' })}
      >
        {modal.step === 'options' ? (
          <div>
            <p>Se incluirán los documentos de la carpeta y sus subcarpetas.</p>
            {/* Estructura (qué carpetas trae el ZIP) va aparte de los
                adjuntos de cada documento. */}
            <div style={sectionStyle}>Estructura</div>
            <Checkbox checked={options.emptyFolders} onChange={e => setOptions({ ...options, emptyFolders: e.target.checked })}>
              Incluir las carpetas vacías
              <div style={hintStyle}>El ZIP tendrá el árbol completo, aunque algunas carpetas no tengan documentos.</div>
            </Checkbox>
            <div style={sectionStyle}>Incluir también en cada documento</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Checkbox checked={options.xml} onChange={e => setOptions({ ...options, xml: e.target.checked })}>Archivos XML</Checkbox>
              <Checkbox checked={options.accounting} onChange={e => setOptions({ ...options, accounting: e.target.checked })}>Contabilización</Checkbox>
              <Checkbox checked={options.links} onChange={e => setOptions({ ...options, links: e.target.checked })}>Archivos vinculados</Checkbox>
            </div>
          </div>
        ) : (
          <div>
            {progress.phase === 'census' ? (
              <p>Preparando la descarga (contando archivos y adjuntos)...</p>
            ) : (
              <>
                <p style={{ marginBottom: 4 }}>📁 Carpeta actual: <strong>{progress.folderLabel}</strong></p>
                <p style={{ marginBottom: 12 }}>📄 Archivo {progress.current} de {progress.total} — {progress.fileName}</p>
              </>
            )}
            <div style={{ background: '#f0f0f0', borderRadius: 4 }}>
              <div style={{ width: `${progress.percent}%`, height: 20, backgroundColor: '#1890ff', borderRadius: 4, transition: 'width 0.3s ease-in-out' }} />
            </div>
            <p>{progress.percent}% completado</p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DownloadFolder;
